import React, { useRef, useEffect, useState, useCallback } from 'react';
import {
  Point2DDoublePoutre,
  MotifPersonnaliseDoublePoutre,
  CourbeBezierDoublePoutre,
  LigneDoublePoutre
} from '../types';
import { CONFIGURATION_CANVAS_2D_TRAVERSES_DOUBLE_POUTRE } from '../index';
import './Canvas2DDoublePoutre.css';

interface Canvas2DDoublePoutreProps {
  motifPersonnalise?: MotifPersonnaliseDoublePoutre;
  onMotifChange: (motif: MotifPersonnaliseDoublePoutre) => void;
  onClose: () => void;
  visible: boolean;
}

interface OutilCanvas {
  type: 'ligne' | 'courbe' | 'effacer' | 'deplacer';
  nom: string;
  icone: string;
}

const OUTILS: OutilCanvas[] = [
  { type: 'ligne', nom: 'Ligne', icone: '📏' },
  { type: 'courbe', nom: 'Courbe', icone: '🌊' },
  { type: 'effacer', nom: 'Effacer', icone: '🗑️' },
  { type: 'deplacer', nom: 'Déplacer', icone: '✋' }
];

/**
 * Éditeur Canvas 2D pour les motifs personnalisés des traverses de la double poutre
 * Conforme aux exigences de la section 3.4 du cahier des charges
 */
export const Canvas2DDoublePoutre: React.FC<Canvas2DDoublePoutreProps> = ({
  motifPersonnalise,
  onMotifChange,
  onClose,
  visible
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [outilActif, setOutilActif] = useState<OutilCanvas['type']>('ligne');
  const [zoom, setZoom] = useState(CONFIGURATION_CANVAS_2D_TRAVERSES_DOUBLE_POUTRE.zoom.defaut);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [isDrawing, setIsDrawing] = useState(false);
  const [pointDebut, setPointDebut] = useState<Point2DDoublePoutre | null>(null);
  const [motifLocal, setMotifLocal] = useState<MotifPersonnaliseDoublePoutre>(
    motifPersonnalise || {
      id: `motif_${Date.now()}`,
      nom: 'Motif personnalisé',
      courbes: [],
      lignes: [],
      dateCreation: new Date().toISOString(),
      dateModification: new Date().toISOString()
    }
  );

  // Dessiner la grille
  const dessinerGrille = useCallback((ctx: CanvasRenderingContext2D) => {
    const config = CONFIGURATION_CANVAS_2D_TRAVERSES_DOUBLE_POUTRE;
    const tailleGrille = config.tailleGrille * zoom;
    
    ctx.strokeStyle = config.couleurGrille;
    ctx.lineWidth = 0.5;
    ctx.setLineDash([]);
    
    // Lignes verticales
    for (let x = offset.x % tailleGrille; x < config.largeur; x += tailleGrille) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, config.hauteur);
      ctx.stroke();
    }
    
    // Lignes horizontales
    for (let y = offset.y % tailleGrille; y < config.hauteur; y += tailleGrille) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(config.largeur, y);
      ctx.stroke();
    }
  }, [zoom, offset]);

  // Dessiner les éléments du motif
  const dessinerMotif = useCallback((ctx: CanvasRenderingContext2D) => {
    const config = CONFIGURATION_CANVAS_2D_TRAVERSES_DOUBLE_POUTRE;
    
    // Dessiner les lignes
    motifLocal.lignes.forEach(ligne => {
      ctx.strokeStyle = ligne.couleur;
      ctx.lineWidth = ligne.epaisseur;
      ctx.setLineDash([]);
      
      ctx.beginPath();
      ctx.moveTo(
        (ligne.debut.x * zoom) + offset.x,
        (ligne.debut.y * zoom) + offset.y
      );
      ctx.lineTo(
        (ligne.fin.x * zoom) + offset.x,
        (ligne.fin.y * zoom) + offset.y
      );
      ctx.stroke();
    });
    
    // Dessiner les courbes
    motifLocal.courbes.forEach(courbe => {
      ctx.strokeStyle = courbe.couleur;
      ctx.lineWidth = courbe.epaisseur;
      ctx.setLineDash([]);
      
      ctx.beginPath();
      ctx.moveTo(
        (courbe.debut.x * zoom) + offset.x,
        (courbe.debut.y * zoom) + offset.y
      );
      ctx.bezierCurveTo(
        (courbe.controle1.x * zoom) + offset.x,
        (courbe.controle1.y * zoom) + offset.y,
        (courbe.controle2.x * zoom) + offset.x,
        (courbe.controle2.y * zoom) + offset.y,
        (courbe.fin.x * zoom) + offset.x,
        (courbe.fin.y * zoom) + offset.y
      );
      ctx.stroke();
    });
  }, [motifLocal, zoom, offset]);

  // Redessiner le canvas
  const redessiner = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    const config = CONFIGURATION_CANVAS_2D_TRAVERSES_DOUBLE_POUTRE;
    
    // Effacer le canvas
    ctx.fillStyle = config.couleurFond;
    ctx.fillRect(0, 0, config.largeur, config.hauteur);
    
    // Dessiner la grille
    dessinerGrille(ctx);
    
    // Dessiner le motif
    dessinerMotif(ctx);
  }, [dessinerGrille, dessinerMotif]);

  // Convertir les coordonnées canvas en coordonnées motif
  const canvasVersMotif = useCallback((x: number, y: number): Point2DDoublePoutre => {
    return {
      x: (x - offset.x) / zoom,
      y: (y - offset.y) / zoom
    };
  }, [zoom, offset]);

  // Gestionnaire de clic sur le canvas
  const handleCanvasClick = useCallback((event: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const rect = canvas.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    const pointMotif = canvasVersMotif(x, y);
    
    if (outilActif === 'ligne') {
      if (!isDrawing) {
        setPointDebut(pointMotif);
        setIsDrawing(true);
      } else {
        if (pointDebut) {
          const nouvelleLigne: LigneDoublePoutre = {
            id: `ligne_${Date.now()}`,
            debut: pointDebut,
            fin: pointMotif,
            couleur: CONFIGURATION_CANVAS_2D_TRAVERSES_DOUBLE_POUTRE.couleurTrait,
            epaisseur: CONFIGURATION_CANVAS_2D_TRAVERSES_DOUBLE_POUTRE.epaisseurTrait
          };
          
          const nouveauMotif = {
            ...motifLocal,
            lignes: [...motifLocal.lignes, nouvelleLigne],
            dateModification: new Date().toISOString()
          };
          
          setMotifLocal(nouveauMotif);
          onMotifChange(nouveauMotif);
        }
        setIsDrawing(false);
        setPointDebut(null);
      }
    }
  }, [outilActif, isDrawing, pointDebut, motifLocal, canvasVersMotif, onMotifChange]);

  // Gestionnaire de zoom
  const handleZoom = useCallback((delta: number) => {
    const config = CONFIGURATION_CANVAS_2D_TRAVERSES_DOUBLE_POUTRE.zoom;
    const nouveauZoom = Math.max(
      config.min,
      Math.min(config.max, zoom + delta)
    );
    setZoom(nouveauZoom);
  }, [zoom]);

  // Effacer le motif
  const effacerMotif = useCallback(() => {
    const motifVide = {
      ...motifLocal,
      lignes: [],
      courbes: [],
      dateModification: new Date().toISOString()
    };
    setMotifLocal(motifVide);
    onMotifChange(motifVide);
  }, [motifLocal, onMotifChange]);

  // Effet pour redessiner quand nécessaire
  useEffect(() => {
    redessiner();
  }, [redessiner]);

  // Initialiser le canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const config = CONFIGURATION_CANVAS_2D_TRAVERSES_DOUBLE_POUTRE;
    canvas.width = config.largeur;
    canvas.height = config.hauteur;
    
    redessiner();
  }, [redessiner]);

  if (!visible) return null;

  return (
    <div className="canvas-2d-double-poutre-overlay">
      <div className="canvas-2d-double-poutre-container">
        <div className="canvas-header">
          <h3>Éditeur de Motif - Traverses Double Poutre</h3>
          <button className="btn-fermer" onClick={onClose}>✕</button>
        </div>
        
        <div className="canvas-toolbar">
          {OUTILS.map(outil => (
            <button
              key={outil.type}
              className={`btn-outil ${outilActif === outil.type ? 'actif' : ''}`}
              onClick={() => setOutilActif(outil.type)}
              title={outil.nom}
            >
              {outil.icone} {outil.nom}
            </button>
          ))}
          
          <div className="separateur"></div>
          
          <div className="controles-zoom">
            <button onClick={() => handleZoom(-0.1)}>🔍-</button>
            <span>{Math.round(zoom * 100)}%</span>
            <button onClick={() => handleZoom(0.1)}>🔍+</button>
          </div>
          
          <div className="separateur"></div>
          
          <button className="btn-effacer" onClick={effacerMotif}>
            🗑️ Effacer tout
          </button>
        </div>
        
        <div className="canvas-zone">
          <canvas
            ref={canvasRef}
            onClick={handleCanvasClick}
            className="canvas-dessin"
          />
        </div>
        
        <div className="canvas-footer">
          <div className="info-motif">
            <span>Lignes: {motifLocal.lignes.length}</span>
            <span>Courbes: {motifLocal.courbes.length}</span>
          </div>
          <div className="actions">
            <button className="btn-annuler" onClick={onClose}>Annuler</button>
            <button className="btn-valider" onClick={onClose}>Valider</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Canvas2DDoublePoutre;